import { ref, watch } from 'vue';
// Forecast & filter state – so we can embed context data in the prompt
import filtersState from './temp-data-and-supports/useForecastFilters.js';
import { selectedRange, selectedMode } from './temp-data-and-supports/useForecastViewState.js';
import { useForecast } from './temp-data-and-supports/useForecastApi.js';
import useTankStats from './temp-data-and-supports/useTankStats.js';

import { getEnv } from '@/env-runtime';


/* --------------------------------------------------------------------------
   useChat.js – minimal reactive chat state + helper to send a message.

   Behaviour changes vs. the initial placeholder:
   • Sends a POST request to the back-end (Azure Functions or another API)
     instead of echoing user input.
   • Gracefully falls back to a generic error message when the request fails
     so the UI never stalls indefinitely.
--------------------------------------------------------------------------- */

// ---------------------------------------------------------------------
// 1️⃣  Environment helpers (mirrors useForecastApi.js so deployment/keys
//     behave consistently across the app)
// ---------------------------------------------------------------------

// Resolve Functions base URL and keys (Vue CLI + Vite compatible)
let API_BASE = (
  getEnv('VITE_FUNCTIONS_BASE') ||
  getEnv('VUE_APP_FUNCTIONS_BASE') ||
  getEnv('VITE_API_BASE') ||
  getEnv('VUE_APP_API_BASE') ||
  ''
).replace(/\/$/, '');
if (!API_BASE) {
  console.warn('[useChat] FUNCTIONS_BASE env missing; defaulting to local Functions host.');
  API_BASE = 'http://localhost:7071/api';
}

const FUNC_KEY = (
  getEnv('VITE_FUNCTIONS_KEY') ||
  getEnv('VUE_APP_FUNCTIONS_KEY') ||
  getEnv('VITE_AZ_FN_KEY') ||
  getEnv('VUE_APP_AZ_FN_KEY') ||
  ''
).trim();

// Typing animation helper – progressively add characters with small delays
function typeText(targetMsg, fullText, delay = 25) {
  return new Promise((resolve) => {
    let idx = 0;
    function tick() {
      targetMsg.content += fullText[idx];
      idx += 1;
      if (idx < fullText.length) {
        setTimeout(tick, delay);
      } else {
        resolve();
      }
    }
    if (fullText && targetMsg) tick(); else resolve();
  });
}

function buildHeaders() {
  return {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...(FUNC_KEY ? { 'x-functions-key': FUNC_KEY } : {}),
  };
}

function withCodeParam(path) {
  return FUNC_KEY ? `${path}${path.includes('?') ? '&' : '?'}code=${encodeURIComponent(FUNC_KEY)}` : path;
}

const CHAT_ENDPOINT = withCodeParam((getEnv('VITE_CHAT_ENDPOINT') || getEnv('VUE_APP_CHAT_ENDPOINT')) ? (getEnv('VITE_CHAT_ENDPOINT') || getEnv('VUE_APP_CHAT_ENDPOINT')).replace(/\/$/, '') : `${API_BASE}/r3moteio_ai/openai/responses`);
export function useChat() {
  // Reactive state -----------------------------------------------------
  const SESSION_KEY = 'ai_chat_history_v1';

  let initialMsgs;
  if (typeof window !== 'undefined' && window.sessionStorage?.getItem(SESSION_KEY)) {
    try {
      initialMsgs = JSON.parse(window.sessionStorage.getItem(SESSION_KEY));
    // eslint-disable-next-line no-empty
    } catch {}
  }

  const messages = ref(
    Array.isArray(initialMsgs) && initialMsgs.length
      ? initialMsgs
      : [
          {
            id: 1,
            role: 'assistant',
            content: 'Hi! How can I help you with inventory forecasting today?',
          },
        ]
  );

  const draft   = ref('');       // textarea binding
  const loading = ref(false);    // controls typing indicator & disable send

  // Shared data we might include in the prompt -----------------------
  const { data: forecastData, load: loadForecast } = useForecast();
  const { totalTanks, criticalTanks } = useTankStats();

  // -------------------------------------------------------------------
  // Helper – actually send user input to the back-end
  // -------------------------------------------------------------------
  async function send() {
    const text = draft.value.trim();
    if (!text || loading.value) return;  // guard clause

    // Push user message immediately so it appears in the UI
    const userMsg = { id: Date.now(), role: 'user', content: text };
    messages.value.push(userMsg);
    draft.value = '';

    // Snapshot conversation **before** adding placeholder assistant.
    // We’ll keep only the most recent messages so the prompt size doesn’t
    // explode and break the back-end (token limit / 500 errors when users ask
    // for very long outputs).

    const RAW_HISTORY = messages.value.map(({ role, content }) => ({ role, content }));

    // Include the full chat history without manual truncation – back-end will
    // handle any necessary token or size limits.
    const conversationSnapshot = [...RAW_HISTORY];

    // --------------------------------------------------------------
    //  Build structured context block from currently selected data
    // --------------------------------------------------------------

    let contextBlock = '';
    const opId   = filtersState.operatorId;
    const chem   = filtersState.chemical;

    if (opId && chem) {
      // Ensure we have forecast data loaded (uses cache if already fetched)
      if (!forecastData.value) {
        await loadForecast(opId, chem);
      }

      const mode    = selectedMode.value;
      const horizon = selectedRange.value;

      contextBlock += '--- APP DATA CONTEXT START ---\n';
      contextBlock += `OperatorID: ${opId}\n`;
      contextBlock += `Chemical: ${chem}\n`;
      contextBlock += `ViewMode: ${mode}\n`;
      contextBlock += `HorizonDays: ${horizon}\n`;

      // Tank stats (cards)
      contextBlock += `TotalTanks: ${totalTanks.value}\n`;
      contextBlock += `CriticalTanks: ${criticalTanks.value}\n`;

      // Forecast data – include small CSV-like list so model can reason
      if (forecastData.value) {
        const metricKey = mode === 'level' ? 'level' : 'consumption';
        const histArr   = forecastData.value?.[metricKey]?.history ?? [];
        const fcArr     = forecastData.value?.[metricKey]?.forecast ?? [];

        const histSlice = histArr.slice(-horizon);
        const fcSlice   = fcArr.slice(0, horizon);

        const histLines = histSlice.map(p => `${p.ds},${p.y}`).join('\n');
        const fcLines   = fcSlice.map(p => `${p.ds},${p.yhat},${p.yhat_lower ?? ''},${p.yhat_upper ?? ''}`).join('\n');

        contextBlock += 'HistoricalData (date,value):\n';
        contextBlock += histLines + '\n';
        contextBlock += 'ForecastData (date,yhat,low,high):\n';
        contextBlock += fcLines + '\n';
      }

      contextBlock += '--- APP DATA CONTEXT END ---\n';
    }

    // Prepare placeholder assistant message (shown while we wait)
    const replyId = Date.now() + 1;
    messages.value.push({ id: replyId, role: 'assistant', content: '' });

    loading.value = true;

    try {
      // ------------------------------------------------------------------
      // Construct request body for legacy "Responses" API
      // ------------------------------------------------------------------

      // 1. System prompt – hard-coded as requested.  Appended TOP of the
      //    conversation so the back-end (and, ultimately, GPT) treats it as
      //    an instruction.
      const SYSTEM_PROMPT =
        'You are an LLM chat assistant to help interpret and assist users with the forecast information displayed in the application, if data is being displayed on the screen the data points will also be passed to you to assist you in your analysis. If no supporting data is provided, you should respond that you have no data to assist with. You should also avoid making up any data points or fabricating information. If the user asks a question that is not related to the inventory forecasting application or the data provided, you should respond that you are unable to assist with that question and guide the user to select the Operator and Chemical from the provided selectors. Keep your answers concise and to the point.';

      const fullInput = [
        `system: ${SYSTEM_PROMPT}`,
        ...(contextBlock ? [`context:\n${contextBlock}`] : []),
        ...conversationSnapshot.map(({ role, content }) => `${role}: ${content}`),
      ].join('\n');

      const payload = {
        model: 'gpt-4.1-mini',
        input: fullInput,

        // Enable the web-search tool so the model can enrich answers.
        // Enable OpenAI web-search tool (correct contract key)
        tools: [
          { type: 'web_search_preview' },
        ],

        stream: false,
      };

      // POST expecting full JSON – no streaming --------------------------------------------------
      const resp = await fetch(CHAT_ENDPOINT, {
        method: 'POST',
        headers: buildHeaders(), // Accept defaults to application/json
        body: JSON.stringify(payload),
      });

      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);

      const json = await resp.json();

      const targetMsg = messages.value.find(m => m.id === replyId);
      if (!targetMsg) return; // bail (shouldn’t happen)

      // --------------------------------------------------------------
      // Extract assistant’s text from the Responses schema, tolerating
      // tool calls (web_search_preview etc.).
      // --------------------------------------------------------------

      const extractAssistantText = (payload) => {
        // Case 1 – standard wrapper with output_text
        if (typeof payload?.output_text === 'string') return payload.output_text;

        // Case 2 – legacy { message: "…" }
        if (typeof payload?.message === 'string') return payload.message;

        // Case 3 – Rich schema: output[] array
        if (Array.isArray(payload?.output)) {
          // Find the first element with type === 'message'
          const msgObj = payload.output.find(o => o.type === 'message' && o.content?.[0]?.text);
          if (msgObj) return msgObj.content[0].text;

          // Fallback – maybe first element has content but different order
          for (const entry of payload.output) {
            if (entry?.content?.[0]?.text) return entry.content[0].text;
          }
        }

        // Nothing matched – stringify for debugging
        return JSON.stringify(payload);
      };

      const assistantText = extractAssistantText(json);

      // Animate character-by-character typing effect
      await typeText(targetMsg, assistantText);

    } catch (err) {
      console.error('[useChat] request failed:', err);
      const targetMsgErr = messages.value.find(m => m.id === replyId);
      if (targetMsgErr) {
        targetMsgErr.content =
          '⚠️ Sorry, I ran into a problem fetching the response. Please try again later.';
      }
    } finally {
      loading.value = false;
    }

  }

  // Keep messages in-sync with sessionStorage for the whole session ----------
  if (typeof window !== 'undefined') {
    watch(
      messages,
      (val) => {
        try {
          window.sessionStorage.setItem(SESSION_KEY, JSON.stringify(val));
        // eslint-disable-next-line no-empty
        } catch {}
      },
      { deep: true }
    );
  }

  // Helper to clear history (keep initial greeting)
  function clear() {
    messages.value = [
      {
        id: Date.now(),
        role: 'assistant',
        content: 'Hi! How can I help you with inventory forecasting today?',
      },
    ];
    if (typeof window !== 'undefined') {
      try {
        window.sessionStorage.removeItem(SESSION_KEY);
      // eslint-disable-next-line no-empty
      } catch {} // ignore quota / unavailable errors
    }
    draft.value = '';
  }

  return { messages, draft, loading, send, clear };
}

export default useChat;
